import {observer} from "mobx-react-lite";
import React, {useEffect, useMemo, useRef, useState} from "react";
import GameStore from "../../store/game";

const IOS_EDGE_GUARD_PX = 20;
const REMOVE_ANIMATION_MS = 220;
const FALL_ANIMATION_MS = 360;
const FALL_DOWN_CLASSES = [
    "",
    "[--fall-y:calc(100%_+_0.375rem)]",
    "[--fall-y:calc(200%_+_0.75rem)]",
    "[--fall-y:calc(300%_+_1.125rem)]",
    "[--fall-y:calc(400%_+_1.5rem)]",
    "[--fall-y:calc(500%_+_1.875rem)]",
    "[--fall-y:calc(600%_+_2.25rem)]",
];
const FALL_UP_CLASSES = [
    "",
    "[--fall-y:calc(-100%_-_0.375rem)]",
    "[--fall-y:calc(-200%_-_0.75rem)]",
    "[--fall-y:calc(-300%_-_1.125rem)]",
    "[--fall-y:calc(-400%_-_1.5rem)]",
    "[--fall-y:calc(-500%_-_1.875rem)]",
    "[--fall-y:calc(-600%_-_2.25rem)]",
];
const ROW_START_CLASSES = ["row-start-1", "row-start-2", "row-start-3", "row-start-4", "row-start-5", "row-start-6"];
const COL_START_CLASSES = ["col-start-1", "col-start-2", "col-start-3", "col-start-4", "col-start-5", "col-start-6"];
// Нейтральные клетки не отвлекают от поиска слов до явного выделения игроком.
const DEFAULT_TILE_CLASSES = "bg-ui-elevated border-ui-default text-ui-primary";

const cellKey = (cell) => `${cell.row}-${cell.col}`;

const isAdjacent = (left, right) => {
    if (!left || !right) return false;
    return Math.max(Math.abs(left.row - right.row), Math.abs(left.col - right.col)) === 1;
};

// selectionDirection возвращает направление между двумя соседними клетками.
const selectionDirection = (left, right) => ({
    dr: right.row - left.row,
    dc: right.col - left.col,
});

// followsSelectionDirection проверяет, что новая клетка продолжает выбранную прямую.
const followsSelectionDirection = (cells, next) => {
    if (cells.length < 2) return true;
    const expected = selectionDirection(cells[0], cells[1]);
    const actual = selectionDirection(cells[cells.length - 1], next);
    return expected.dr === actual.dr && expected.dc === actual.dc;
};

// Поле падающих букв: drag проходит по соседним клеткам, а обновлённые сервером столбцы плавно падают.
export const GameBoard = observer(({
    onSelectionChange,
    onSelectionComplete,
    isDisabled,
    hintRevealPath = [],
    boardTransition = null,
    onBoardTransitionComplete,
}) => {
    const [transitionPhase, setTransitionPhase] = useState("idle");
    const boardRef = useRef(null);
    const isSelectingRef = useRef(false);
    const selectionRef = useRef([]);
    const activePointerRef = useRef(null);
    const removalTimerRef = useRef(null);
    const fallingTimerRef = useRef(null);

    const selectedCellSet = useMemo(
        () => new Set((GameStore.selectionCells || []).map(cellKey)),
        [GameStore.selectionCells],
    );
    const hintCellSet = useMemo(
        () => new Set((hintRevealPath || []).map((item) => `${Number(item?.row)}-${Number(item?.col)}`)),
        [hintRevealPath],
    );

    useEffect(() => {
        if (!boardTransition) return undefined;

        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
        const removeDuration = reduceMotion ? 80 : REMOVE_ANIMATION_MS;
        const fallDuration = reduceMotion ? 80 : FALL_ANIMATION_MS;
        setTransitionPhase("removing");
        removalTimerRef.current = setTimeout(() => {
            setTransitionPhase("falling");
            fallingTimerRef.current = setTimeout(() => {
                setTransitionPhase("idle");
                onBoardTransitionComplete?.(boardTransition);
            }, fallDuration);
        }, removeDuration);

        return () => {
            if (removalTimerRef.current) clearTimeout(removalTimerRef.current);
            if (fallingTimerRef.current) clearTimeout(fallingTimerRef.current);
        };
    }, [boardTransition, onBoardTransitionComplete]);

    const isEdgeGestureStart = (clientX) => (
        Number.isFinite(clientX)
        && typeof window !== "undefined"
        && (clientX <= IOS_EDGE_GUARD_PX || clientX >= window.innerWidth - IOS_EDGE_GUARD_PX)
    );

    const finishSelection = (pointerId, shouldSubmit = true) => {
        if (!isSelectingRef.current || (activePointerRef.current !== null && pointerId !== activePointerRef.current)) return;

        const cells = [...selectionRef.current];
        if (boardRef.current?.hasPointerCapture?.(pointerId)) {
            boardRef.current.releasePointerCapture(pointerId);
        }
        activePointerRef.current = null;
        isSelectingRef.current = false;
        if (shouldSubmit && cells.length >= 3) {
            onSelectionComplete?.(cells);
        } else {
            onSelectionChange?.([]);
        }
    };

    const extendSelection = (next) => {
        const current = [...selectionRef.current];
        if (current.length === 0) {
            selectionRef.current = [next];
            onSelectionChange?.([next]);
            return;
        }

        const existingIndex = current.findIndex((item) => item.row === next.row && item.col === next.col);
        if (existingIndex !== -1 && existingIndex === current.length - 2) {
            const shortened = current.slice(0, -1);
            selectionRef.current = shortened;
            onSelectionChange?.(shortened);
            return;
        }
        if (
            existingIndex !== -1
            || !isAdjacent(current[current.length - 1], next)
            || !followsSelectionDirection(current, next)
        ) return;

        const extended = [...current, next];
        selectionRef.current = extended;
        onSelectionChange?.(extended);
    };

    const cellFromPoint = (clientX, clientY) => {
        // При захвате указателя некоторые WebView возвращают стартовую кнопку из elementFromPoint.
        // Геометрический поиск сохраняет корректное выделение при движении пальца по полю.
        const elements = boardRef.current?.querySelectorAll?.("[data-falling-cell='true']") || [];
        const element = [...elements].find((item) => {
            const rect = item.getBoundingClientRect();
            return clientX >= rect.left
                && clientX <= rect.right
                && clientY >= rect.top
                && clientY <= rect.bottom;
        });
        if (!element) return null;
        const row = Number(element.dataset.row);
        const col = Number(element.dataset.col);
        return Number.isFinite(row) && Number.isFinite(col) ? {row, col} : null;
    };

    const handlePointerDown = (event, row, col) => {
        if (isDisabled || isSelectingRef.current || isEdgeGestureStart(event.clientX)) return;
        event.preventDefault();
        activePointerRef.current = event.pointerId;
        isSelectingRef.current = true;
        selectionRef.current = [{row, col}];
        boardRef.current?.setPointerCapture?.(event.pointerId);
        onSelectionChange?.(selectionRef.current);
    };

    const handlePointerMove = (event) => {
        if (!isSelectingRef.current || isDisabled || event.pointerId !== activePointerRef.current) return;
        if (event.cancelable) event.preventDefault();
        const next = cellFromPoint(event.clientX, event.clientY);
        if (next) extendSelection(next);
    };

    if (!Array.isArray(GameStore.grid) || GameStore.grid.length === 0) {
        return <div className="px-4 py-3 text-sm text-gray-400 text-center">Поле загружается...</div>;
    }

    const removedSet = new Set((boardTransition?.removedCells || []).map(cellKey));
    const affectedColumns = new Set((boardTransition?.removedCells || []).map((item) => item.col));
    const removedCountByColumn = new Map();
    (boardTransition?.removedCells || []).forEach((item) => {
        removedCountByColumn.set(item.col, (removedCountByColumn.get(item.col) || 0) + 1);
    });
    const fallRowsForCell = (row, col) => (boardTransition?.removedCells || [])
        .filter((item) => item.col === col && item.row > row)
        .length;

    return (
        <div className="px-5 sm:px-4">
            <div
                ref={boardRef}
                className="arcade-panel grid grid-cols-6 gap-2 select-none touch-none overscroll-contain p-3 w-full max-w-[22rem] mx-auto"
                onPointerMove={handlePointerMove}
                onPointerUp={(event) => finishSelection(event.pointerId, true)}
                onPointerCancel={(event) => finishSelection(event.pointerId, false)}
            >
                {GameStore.grid.map((row, rowIndex) => row.map((cell, colIndex) => {
                    const key = `${rowIndex}-${colIndex}`;
                    const isSelected = selectedCellSet.has(key) && transitionPhase !== "falling";
                    const isHint = hintCellSet.has(key) && !isSelected;
                    const isRemoved = removedSet.has(key);
                    const fallRows = fallRowsForCell(rowIndex, colIndex);
                    // На фазе падения показываем итоговую букву в снятой клетке, чтобы поле не мигало пустотой.
                    const nextCell = boardTransition?.nextGrid?.[rowIndex]?.[colIndex] || "";
                    const displayCell = transitionPhase === "falling" && isRemoved && nextCell ? nextCell : cell;
                    const classes = [
                        "arcade-tile aspect-square rounded-ui border-2 text-base uppercase flex items-center justify-center !p-0 !m-0 min-h-0 appearance-none font-black",
                        "transition-[background-color,transform,opacity] duration-150",
                        isSelected
                            ? "arcade-tile--selected bg-brand-success border-yellow-300 !text-yellow-950 scale-95 ring-2 ring-brand-success/50"
                            : DEFAULT_TILE_CLASSES,
                        isHint ? "arcade-tile--hint ring-2 ring-yellow-500/80 ring-inset" : "",
                        transitionPhase === "removing" && isRemoved
                            ? "animate-falling-remove motion-reduce:animate-falling-remove-reduced"
                            : "",
                        transitionPhase === "falling" && isRemoved ? "z-[1]" : "",
                        transitionPhase === "falling" && affectedColumns.has(colIndex) && fallRows > 0
                            ? `animate-falling-survivor motion-reduce:animate-falling-survivor-reduced z-[2] ${FALL_DOWN_CLASSES[fallRows]}`
                            : "",
                        ROW_START_CLASSES[rowIndex],
                        COL_START_CLASSES[colIndex],
                    ].join(" ");

                    return (
                        <button
                            type="button"
                            key={key}
                            data-falling-cell="true"
                            data-row={rowIndex}
                            data-col={colIndex}
                            className={classes}
                            disabled={isDisabled || !displayCell}
                            aria-label={`Строка ${rowIndex + 1}, столбец ${colIndex + 1}, буква ${displayCell}`}
                            onPointerDown={(event) => handlePointerDown(event, rowIndex, colIndex)}
                        >
                            <span>{displayCell}</span>
                        </button>
                    );
                }))}
                {transitionPhase === "falling" && boardTransition
                    ? [...affectedColumns].flatMap((colIndex) => {
                        const count = removedCountByColumn.get(colIndex) || 0;
                        return Array.from({length: count}, (_, rowIndex) => (
                            <div
                                key={`new-${colIndex}-${rowIndex}`}
                                className={`arcade-tile animate-falling-new motion-reduce:animate-falling-new-reduced z-[3] aspect-square rounded-ui border-2 text-base uppercase flex items-center justify-center ${DEFAULT_TILE_CLASSES} font-black ${ROW_START_CLASSES[rowIndex]} ${COL_START_CLASSES[colIndex]} ${FALL_UP_CLASSES[count - rowIndex]}`}
                                aria-hidden="true"
                            >
                                <span>{boardTransition.nextGrid[rowIndex]?.[colIndex]}</span>
                            </div>
                        ));
                    })
                    : null}
            </div>
        </div>
    );
});
